import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import {
  CollectionRole,
  CollectionSortOrder,
  type CollectionStatus,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useProductTypes } from '@/features/dictionary';
import { MediaRichTextEditor, useMedia } from '@/features/media';
import { RulesBuilder, useRuleFields, type RuleValueKind } from '@/features/product-rules';
import { slugify } from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  useSeoStoreContext,
} from '@/features/seo';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  collectionFormSchema,
  type CollectionFormInput,
  type CollectionFormOutput,
} from '../schemas/collection-form-schema';

import { CollectionImageField } from './collection-image-field';
import { CollectionKindBadge } from './collection-kind-badge';
import { RulesPreview } from './rules-preview';

export const COLLECTION_FORM_ID = 'collection-form';

export interface CollectionFormProps {
  storeId: string;
  currency: string;
  mode: 'create' | 'edit';
  defaultValues: CollectionFormInput;
  isSubmitting: boolean;
  /** Receives the validated values and the field-kind resolver the mappers need. */
  onSubmit: (
    values: CollectionFormOutput,
    kindOf: (field: string) => RuleValueKind,
  ) => Promise<unknown>;
  /** Sections under the form in the main column: the product list. */
  children?: ReactNode;
  /** Extra side-rail cards under sort order: refresh state. */
  aside?: ReactNode;
  dangerZone?: ReactNode;
}

const STATUS_ORDER: CollectionStatus[] = ['active', 'hidden'];

const NO_PRODUCT_TYPE = '__none__';

/**
 * Two columns: details, rules (automated), image and SEO inside the <form>; status, sort order
 * and the save button in the side rail bound to the same react-hook-form context. Non-form
 * sections (the product list) are siblings so their buttons never submit the collection.
 */
export function CollectionForm({
  storeId,
  currency,
  mode,
  defaultValues,
  isSubmitting,
  onSubmit,
  children,
  aside,
  dangerZone,
}: CollectionFormProps) {
  const { t } = useTranslation(['collections', 'common']);
  const message = useErrorMessage();
  const form = useForm<CollectionFormInput, unknown, CollectionFormOutput>({
    resolver: useI18nResolver(collectionFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const { fields, kindOf } = useRuleFields();
  const [handleTouched, setHandleTouched] = useState(mode === 'edit');
  const kind = form.watch('kind');
  const sortOrder = form.watch('sortOrder');
  const role = form.watch('role');
  const productTypes = useProductTypes();
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;
  const seoStore = useSeoStoreContext(storeId);
  const [title, handle, descriptionHtml, status, mediaId, seo] = form.watch([
    'title',
    'handle',
    'descriptionHtml',
    'status',
    'mediaId',
    'seo',
  ]);
  const image = useMedia(mediaId);
  const path = `/collections/${handle}`;
  const live = seoStore
    ? resolveSeoPreview(
        {
          title,
          description: descriptionHtml || null,
          path,
          published: status === 'active',
          override: seo,
          image: mediaId ? { alt: image.data?.alt ?? null } : 'none',
        },
        seoStore,
      )
    : null;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values, kindOf);
      if (mode === 'edit') form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });
  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  const sortOrders = Object.values(CollectionSortOrder).filter(
    (order) => kind === 'manual' || order !== 'manual',
  );

  return (
    <Form {...form}>
      <FormLayout
        aside={
          <>
            <FormSection
              title={t('collections:sections.status')}
              tour="collections.edit.status-save"
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('collections:create.kind')}</span>
                <CollectionKindBadge kind={kind} />
              </div>
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                      >
                        {STATUS_ORDER.map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`collection-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`collection-status-${status}`}>
                                {t(`collections:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`collections:statusHint.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="collections.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={COLLECTION_FORM_ID}
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('collections:create.submit')
                        : t('common:actions.save')}
                  </Button>
                  {mode === 'edit' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('collections:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>

            <FormSection
              title={t('collections:sections.sortOrder')}
              tour="collections.edit.sort-order"
            >
              <FormField
                control={form.control}
                name="sortOrder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('collections:fields.sortOrder')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {sortOrders.map((order) => (
                          <SelectItem key={order} value={order}>
                            {t(`collections:sortOrder.${order}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`collections:sortOrderHint.${sortOrder}`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection
              title={t('collections:sections.placement')}
              description={t('collections:sections.placementDescription')}
              tour="collections.edit.placement"
            >
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('collections:fields.role')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        if (value !== 'category') {
                          form.setValue('productTypeId', null, { shouldDirty: true });
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(CollectionRole).map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`collections:role.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t(`collections:roleHint.${role}`)}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {role === 'category' ? (
                <FormField
                  control={form.control}
                  name="productTypeId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('collections:fields.productType')}</FormLabel>
                      <Select
                        value={field.value ?? NO_PRODUCT_TYPE}
                        onValueChange={(value) =>
                          field.onChange(value === NO_PRODUCT_TYPE ? null : value)
                        }
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NO_PRODUCT_TYPE}>
                            {t('collections:fields.productTypeNone')}
                          </SelectItem>
                          {(productTypes.data ?? []).map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>{t('collections:fields.productTypeHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
              <FormField
                control={form.control}
                name="eyebrow"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('collections:fields.eyebrow')}</FormLabel>
                    <FormControl>
                      <Input {...field} maxLength={120} />
                    </FormControl>
                    <FormDescription>{t('collections:fields.eyebrowHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
            {aside}
            {dangerZone}
          </>
        }
      >
        <form id={COLLECTION_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection
            tour="collections.edit.details"
            title={t('collections:sections.details')}
            description={t('collections:sections.detailsDescription')}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('collections:fields.title')}</FormLabel>
                    <FormControl>
                      <Input
                        autoFocus={mode === 'create'}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!handleTouched) {
                            form.setValue('handle', slugify(e.target.value), {
                              shouldDirty: true,
                            });
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="handle"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('collections:fields.handle')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="font-mono text-[13px]"
                        onChange={(e) => {
                          setHandleTouched(true);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormDescription>{t('collections:fields.handleHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="descriptionHtml"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>{t('collections:fields.description')}</FormLabel>
                    <FormControl>
                      <MediaRichTextEditor
                        value={field.value}
                        onChange={(html) => field.onChange(html ?? '')}
                        onBlur={field.onBlur}
                        aria-label={t('collections:fields.description')}
                      />
                    </FormControl>
                    <FormDescription>{t('collections:fields.descriptionHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          {kind === 'automated' ? (
            <>
              <RulesBuilder
                fields={fields}
                kindOf={kindOf}
                currency={currency}
                title={t('collections:sections.rules')}
                description={t('collections:sections.rulesDescription')}
                tour="collections.edit.rules"
              />
              <RulesPreview storeId={storeId} kindOf={kindOf} />
            </>
          ) : null}
          <CollectionImageField />
          <div className="flex flex-col gap-6" data-tour="collections.edit.seo">
            <SeoOverrideFields />
            <SeoPreviewCard live={live} saved={null} path={path} />
          </div>
        </form>
        {children}
      </FormLayout>
    </Form>
  );
}
